import { splitIdentifierUserErrors } from "@repo/components/src/forms/identifier/logic";
import { TaxProfileFieldKeyEnum } from "@repo/graphql/gql/graphql";
import { findField, formNameForKey } from "./fields";
import type { TaxProfileFieldData, TaxProfileFormValues } from "./types";

type UserErrorLike = { field: string[]; message: string };

export type MappedUserErrors = {
  /** Shown under the tax ID input. */
  identifierMessage: string | null;
  /** Shown under the input they name. */
  fieldErrors: { name: keyof TaxProfileFormValues; message: string }[];
  /** Errors that name no field of the form (the country, the address...). */
  toastMessages: string[];
};

// The backend names a field by its key, lower-cased (`TAX_REGIME` ->
// `tax_regime`). A payer's one-line address comes back as plain `address`.
const fieldForServerName = (
  serverName: string,
  fields: ReadonlyArray<TaxProfileFieldData>,
): TaxProfileFieldData | undefined =>
  fields.find((field) => field.key.toLowerCase() === serverName) ??
  (serverName === "address"
    ? findField(fields, TaxProfileFieldKeyEnum.AddressLine)
    : undefined);

/** Sorts the `userErrors` of a save into where each one is shown. */
export const mapTaxProfileUserErrors = <T extends UserErrorLike>(
  userErrors: T[],
  fields: ReadonlyArray<TaxProfileFieldData>,
): MappedUserErrors => {
  const { identifierMessage, otherErrors } =
    splitIdentifierUserErrors(userErrors);
  const fieldErrors: MappedUserErrors["fieldErrors"] = [];
  const toastMessages: string[] = [];
  for (const userError of otherErrors) {
    const serverName = userError.field[userError.field.length - 1];
    const field = serverName
      ? fieldForServerName(serverName, fields)
      : undefined;
    const name = field
      ? formNameForKey(field.key)
      : serverName === "acknowledged"
        ? "acknowledged"
        : undefined;
    if (name) {
      fieldErrors.push({ name, message: userError.message });
    } else {
      toastMessages.push(userError.message);
    }
  }
  return { identifierMessage, fieldErrors, toastMessages };
};
